import { useCallback } from 'react';
import { useTranslation } from '@frontend/i18n';
import { useIsCompactMode } from '@frontend/responsiveness';
import { SurfaceHeader } from '@frontend/surface-header';
import { PaginatedTaskList, taskCenterTrackingIds, TaskListFilters } from '@frontend/task-center-components';
import { useTaskCenterStore } from '@frontend/task-center-scope';
import { Tabs } from '@frontend/design-system';
import { TaskCenterTabs } from '../../constants';
import { TaskAssigneeDropdown } from './task-assignee-dropdown';
import { containerStyle, tabButtonStyle, taskListContainer } from './task-list.styles';

export const TaskList = () => {
  const { t } = useTranslation('task-center');
  const isCompactMode = useIsCompactMode();
  const {
    taskMode,
    setTaskMode,
    setTab,
    selectedTab,
    assignees,
    assigneeFilter,
    setAssigneeFilter,
    setIsTaskCenterOpen,
  } = useTaskCenterStore([
    'taskMode',
    'setTaskMode',
    'setTab',
    'selectedTab',
    'assignees',
    'assigneeFilter',
    'setAssigneeFilter',
    'setIsTaskCenterOpen',
  ]);

  const openCreateTask = useCallback(() => setTaskMode('create'), [setTaskMode]);

  return (
    <div css={[containerStyle, taskMode && { display: 'none' }]}>
      <SurfaceHeader.Root>
        <SurfaceHeader.Row>
          <SurfaceHeader.Start>
            {isCompactMode && (
              <SurfaceHeader.Icon
                iconName='caret-left-small'
                onClick={() => setIsTaskCenterOpen(false)}
                label={t('Back')}
              />
            )}
          </SurfaceHeader.Start>
          <SurfaceHeader.Title>
            {t('Tasks')}
            <TaskAssigneeDropdown
              assignees={assignees}
              assigneeFilter={assigneeFilter}
              setAssigneeFilter={setAssigneeFilter}
            />
          </SurfaceHeader.Title>
          <SurfaceHeader.Actions>
            <TaskListFilters />
            <SurfaceHeader.Icon
              iconName='draft'
              onClick={openCreateTask}
              trackingId={taskCenterTrackingIds.createTaskDraftButton}
              label={t('Create Task')}
              hoverLabel={t('Create Task')}
            />
            {!isCompactMode && (
              <>
                <SurfaceHeader.Divider />
                <SurfaceHeader.Icon iconName='x' onClick={() => setIsTaskCenterOpen(false)} />
              </>
            )}
          </SurfaceHeader.Actions>
        </SurfaceHeader.Row>
      </SurfaceHeader.Root>
      <Tabs initialTab={selectedTab} controlledTab={selectedTab} onChange={(id) => setTab(id)}>
        <Tabs.Bar>
          {TaskCenterTabs.map(({ title, controller, trackingId }) => (
            <Tabs.Tab
              id={controller}
              css={tabButtonStyle}
              key={`tab-${controller}`}
              trackingId={trackingId}
              aria-label={t('{{tab}} tab', { tab: title })}
            >
              {title}
            </Tabs.Tab>
          ))}
        </Tabs.Bar>
        {TaskCenterTabs.map(({ status, controller }) => (
          <Tabs.Panel controller={controller} key={`tab-panel-${controller}`} css={taskListContainer}>
            <PaginatedTaskList
              status={status}
              listQueryEnabled={selectedTab === controller}
              onCreateTask={openCreateTask}
            />
          </Tabs.Panel>
        ))}
      </Tabs>
    </div>
  );
};
